/**
 * Accès au module paramétrage. Réservé à `admin-totalenergies` — le serveur l'impose déjà, ces
 * hooks ne sont utilisés que par des écrans eux-mêmes accessibles au seul administrateur.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, requete } from "../../shared/api/client";
import type {
  CreationModeleExport,
  CreationUtilisateur,
  EntreeAudit,
  MiseAJourModeleExport,
  MiseAJourRegleAlerte,
  MiseAJourSeuil,
  MiseAJourUtilisateur,
  ModeleExportPublie,
  RegleAlertePubliee,
  SeuilPublie,
  SourceExportPubliee,
  UtilisateurCompte,
  ValeurReferentiel,
  ValeurReferentielPubliee,
} from "../../shared/api/types";

type Carte<T> = Record<string, T>;

export function useReferentiel(type: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["admin", "referentiel", type],
    queryFn: () => api.lire<Carte<ValeurReferentielPubliee>>(`/v1/admin/referentiels/${type}`),
    enabled: options?.enabled,
  });
}

export function useEnregistrerValeurReferentiel(type: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ code, valeur }: { code: string; valeur: ValeurReferentiel }) =>
      api.remplacer<ValeurReferentiel & { code: string }>(
        `/v1/admin/referentiels/${type}/${encodeURIComponent(code)}`,
        valeur,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "referentiel", type] });
    },
  });
}

export function useDesactiverValeurReferentiel(type: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.supprimer(`/v1/admin/referentiels/${type}/${encodeURIComponent(code)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "referentiel", type] });
    },
  });
}

export function useSeuils() {
  return useQuery({
    queryKey: ["admin", "seuils"],
    queryFn: () => api.lire<Carte<SeuilPublie>>("/v1/admin/seuils"),
  });
}

export function useModifierSeuil() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ indicateur, corps }: { indicateur: string; corps: MiseAJourSeuil }) =>
      api.remplacer(`/v1/admin/seuils/${encodeURIComponent(indicateur)}`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "seuils"] });
    },
  });
}

export function useReglesAlerte() {
  return useQuery({
    queryKey: ["admin", "alertes-regles"],
    queryFn: () => api.lire<Carte<RegleAlertePubliee>>("/v1/admin/alertes-regles"),
  });
}

export function useModifierRegleAlerte() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ type, corps }: { type: string; corps: MiseAJourRegleAlerte }) =>
      api.remplacer(`/v1/admin/alertes-regles/${encodeURIComponent(type)}`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "alertes-regles"] });
    },
  });
}

export function useModelesExport() {
  return useQuery({
    queryKey: ["admin", "exports-modeles"],
    queryFn: () => api.lire<Carte<ModeleExportPublie>>("/v1/admin/exports-modeles"),
  });
}

export function useSourcesExport() {
  return useQuery({
    // Catalogue fixe des six livrables et de leurs colonnes : pas besoin de le relire à chaque
    // changement d'onglet.
    queryKey: ["admin", "exports-sources"],
    queryFn: () => api.lire<SourceExportPubliee[]>("/v1/admin/exports-sources"),
    staleTime: 5 * 60_000,
  });
}

export function useCreerModeleExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: CreationModeleExport) =>
      api.creer<ModeleExportPublie>("/v1/admin/exports-modeles", corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "exports-modeles"] });
    },
  });
}

export function useModifierModeleExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ modeleId, corps }: { modeleId: string; corps: MiseAJourModeleExport }) =>
      api.remplacer(`/v1/admin/exports-modeles/${encodeURIComponent(modeleId)}`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "exports-modeles"] });
    },
  });
}

export function useDesactiverModeleExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (modeleId: string) =>
      api.supprimer(`/v1/admin/exports-modeles/${encodeURIComponent(modeleId)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "exports-modeles"] });
    },
  });
}

export function useUtilisateurs() {
  return useQuery({
    queryKey: ["admin", "utilisateurs"],
    queryFn: () => api.lire<UtilisateurCompte[]>("/v1/admin/utilisateurs"),
  });
}

export function useCreerUtilisateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: CreationUtilisateur) =>
      api.creer<UtilisateurCompte>("/v1/admin/utilisateurs", corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "utilisateurs"] });
    },
  });
}

export function useModifierUtilisateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sub, corps }: { sub: string; corps: MiseAJourUtilisateur }) =>
      api.modifier<UtilisateurCompte>(`/v1/admin/utilisateurs/${encodeURIComponent(sub)}`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "utilisateurs"] });
    },
  });
}

export function useAudit(filtres: { entite?: string; acteur?: string; action?: string }) {
  return useQuery({
    queryKey: ["admin", "audit", filtres],
    queryFn: () =>
      api.lire<EntreeAudit[]>(
        "/v1/admin/audit" +
          requete({
            "filter[entite]": filtres.entite,
            "filter[acteur]": filtres.acteur,
            "filter[action]": filtres.action,
            pageSize: 100,
          }),
      ),
  });
}
